// =====================================================================
//  FICHA DE LA CONCESIÓN — el cajón lateral
//
//  Réplica de abrir() del prototipo, más lo que la base de datos hace
//  posible: acreditación del pago del doble, registro de adjudicación,
//  verificación de vigencia e historial por persona.
//
//  Los cambios que el motor valida en conjunto se envían en conjunto. Es
//  el caso de RETIRADA: el trigger exige fecha de pago y monto ≥ 2× lo
//  adeudado en la MISMA operación, así que no se puede guardar el estado
//  primero y la acreditación después. La interfaz junta los tres campos y
//  los manda de una vez.
// =====================================================================

import { useEffect, useMemo, useState } from 'react'
import {
  useChecklist,
  useChecklistItems,
  useConcesiones,
  useCriterios,
  useDetallePuntaje,
  useGuardarChecklist,
  useGuardarEvaluacion,
  useGuardarSeguimiento,
  useHistorial,
  usePresupuesto,
  useVerificarVigencia,
  useCorregirConcesion,
} from '../datos/consultas'
import { Alerta, Campo, Cargando, Fila2, Tag, useAvisar, type EstadoGuardado } from '../componentes/ui'
import { calcularPuntaje, percentilesPorCausa, NEUTRO } from '../dominio/calculo'
import { colorPuntaje, fecha, fechaHora, fechaISO, fmt, fmtUtm, num } from '../dominio/formato'
import {
  ESTADOS,
  INTERES,
  type CambioSeguimiento,
  type Concesion,
  type EstadoConcesion,
  type NivelInteres,
  type RolUsuario,
} from '../tipos/dominio'

/** Estados cuya validación necesita campos adicionales en la misma operación. */
const NECESITA_ACREDITACION = (e: EstadoConcesion) =>
  e === 'RETIRADA' || e === 'ADJ_NOSOTROS' || e === 'ADJ_TERCERO'

export function Ficha({
  rolNacional,
  rolUsuario,
  cerrar,
}: {
  rolNacional: string
  rolUsuario: RolUsuario
  cerrar: () => void
}) {
  const concesiones = useConcesiones()
  const criterios = useCriterios()
  const presupuesto = usePresupuesto()
  const avisar = useAvisar()

  const c = concesiones.data?.find((x) => x.rol_nacional === rolNacional)
  const detalle = useDetallePuntaje(c?.id ?? null)
  const items = useChecklistItems()
  const checklist = useChecklist(c?.id ?? null)
  const historial = useHistorial(c?.id ?? null)

  const guardar = useGuardarSeguimiento()
  const guardarEval = useGuardarEvaluacion()
  const guardarChk = useGuardarChecklist()
  const verificar = useVerificarVigencia()

  const puedeEditar = rolUsuario === 'MASTER' || rolUsuario === 'ANALISTA'

  // Valores locales de los sliders, para que el puntaje responda al
  // instante sin esperar el viaje al servidor.
  const [manuales, setManuales] = useState<Record<string, number>>({})
  const [estadoCampo, setEstadoCampo] = useState<Record<string, EstadoGuardado>>({})

  // Borrador del cambio de estado que necesita acreditación.
  const [borrador, setBorrador] = useState<null | {
    estado: EstadoConcesion
    fecha_pago_patente: string
    monto_pagado: string
    monto_adjudicacion: string
    adjudicatario: string
  }>(null)

  useEffect(() => {
    const desdeSql: Record<string, number> = {}
    for (const d of detalle.data ?? []) {
      if (!d.automatico) desdeSql[d.codigo] = num(d.valor)
    }
    setManuales(desdeSql)
  }, [detalle.data])

  useEffect(() => {
    const alTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar()
    }
    window.addEventListener('keydown', alTecla)
    return () => window.removeEventListener('keydown', alTecla)
  }, [cerrar])

  const percentil = useMemo(() => {
    if (!concesiones.data || !c) return 0.5
    return percentilesPorCausa(concesiones.data).get(c.id) ?? 0.5
  }, [concesiones.data, c])

  if (concesiones.isLoading) {
    return (
      <div id="ficha" onClick={cerrar}>
        <div className="drawer">
          <Cargando />
        </div>
      </div>
    )
  }

  if (!c) {
    return (
      <div id="ficha" onClick={(e) => e.target === e.currentTarget && cerrar()}>
        <div className="drawer" style={{ position: 'relative' }}>
          <button className="x" onClick={cerrar}>
            ×
          </button>
          <Alerta tono="bad">
            No se encontró ninguna concesión con rol nacional <b>{rolNacional}</b>.
          </Alerta>
        </div>
      </div>
    )
  }

  const minimo = num(c.minimo_subasta)
  const puntaje = criterios.data
    ? calcularPuntaje(c, criterios.data, { percentil, presupuesto: num(presupuesto.data?.presupuesto) }, manuales)
    : 0

  const marcar = (campo: string, estado: EstadoGuardado) => {
    setEstadoCampo((prev) => ({ ...prev, [campo]: estado }))
    if (estado === 'guardado') {
      setTimeout(() => setEstadoCampo((prev) => ({ ...prev, [campo]: 'quieto' })), 2500)
    }
  }

  /** Guarda un campo suelto. Los errores del motor se traducen y se muestran. */
  const guardarCampo = async (campo: keyof CambioSeguimiento, valor: unknown) => {
    marcar(campo, 'guardando')
    try {
      await guardar.mutateAsync({ id: c.id, cambio: { [campo]: valor } as CambioSeguimiento })
      marcar(campo, 'guardado')
    } catch (e) {
      marcar(campo, 'error')
      avisar(e)
    }
  }

  const cambiarEstado = (nuevo: EstadoConcesion) => {
    if (nuevo === c.estado) return
    if (NECESITA_ACREDITACION(nuevo)) {
      // No se manda todavía: primero hay que acreditar.
      setBorrador({
        estado: nuevo,
        fecha_pago_patente: fechaISO(c.fecha_pago_patente),
        monto_pagado: nuevo === 'RETIRADA' ? String(minimo * 2) : '',
        monto_adjudicacion: nuevo === 'RETIRADA' ? '' : String(minimo),
        adjudicatario: c.adjudicatario ?? (nuevo === 'ADJ_NOSOTROS' ? 'Consorcio Salinero' : ''),
      })
      return
    }
    // TERRENO_FRANCO es incompatible con tener monto de adjudicación, así
    // que se limpia en la misma operación.
    const cambio: CambioSeguimiento =
      nuevo === 'TERRENO_FRANCO'
        ? { estado: nuevo, monto_adjudicacion: null, adjudicatario: null }
        : nuevo === 'EN_REMATE'
          ? { estado: nuevo, fecha_pago_patente: null, monto_pagado: null }
          : { estado: nuevo }
    marcar('estado', 'guardando')
    guardar
      .mutateAsync({ id: c.id, cambio })
      .then(() => marcar('estado', 'guardado'))
      .catch((e) => {
        marcar('estado', 'error')
        avisar(e)
      })
  }

  const confirmarBorrador = async () => {
    if (!borrador) return
    const cambio: CambioSeguimiento =
      borrador.estado === 'RETIRADA'
        ? {
            estado: 'RETIRADA',
            fecha_pago_patente: borrador.fecha_pago_patente || null,
            monto_pagado: borrador.monto_pagado ? Number(borrador.monto_pagado) : null,
          }
        : {
            estado: borrador.estado,
            monto_adjudicacion: borrador.monto_adjudicacion
              ? Number(borrador.monto_adjudicacion)
              : null,
            adjudicatario: borrador.adjudicatario || null,
          }
    marcar('estado', 'guardando')
    try {
      await guardar.mutateAsync({ id: c.id, cambio })
      marcar('estado', 'guardado')
      setBorrador(null)
    } catch (e) {
      marcar('estado', 'error')
      avisar(e)
    }
  }

  const cumplidos = new Set(
    (checklist.data ?? []).filter((x) => x.cumplido).map((x) => x.item_id),
  )

  const automaticos = (detalle.data ?? []).filter((d) => d.automatico)
  const manualesDef = (detalle.data ?? []).filter((d) => !d.automatico)

  return (
    <div id="ficha" onClick={(e) => e.target === e.currentTarget && cerrar()}>
      <div className="drawer" style={{ position: 'relative' }}>
        <button className="x" onClick={cerrar} title="Cerrar (Esc)">
          ×
        </button>

        <h3>{c.nombre}</h3>
        <div className="sub mono" style={{ marginTop: 4 }}>
          Rol nacional {c.rol_nacional} · {c.comuna} · N° {c.numero_orden} de la nómina{' '}
          {c.nomina_numero}
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <Tag clase={ESTADOS[c.estado].c}>{ESTADOS[c.estado].t}</Tag>
          <Tag clase={INTERES[c.interes].c}>Interés {INTERES[c.interes].t}</Tag>
          <Tag clase="t-tercero">Puntaje {puntaje}/100</Tag>
          {c.lote ? (
            <Tag clase={c.lote_confirmado ? 't-pagada' : 't-media'}>
              {c.lote}
              {c.lote_confirmado ? '' : ' (sin confirmar)'}
            </Tag>
          ) : null}
        </div>

        {/* ---------------- Antecedentes ---------------- */}
        <div className="sect" style={{ border: 0, paddingTop: 16, marginTop: 8 }}>
          <h4>Antecedentes de la nómina TGR</h4>
          <dl className="dl">
            <dt>Ubicación</dt>
            <dd>{c.ubicacion ?? '—'}</dd>
            <dt>Titular</dt>
            <dd>{c.titular}</dd>
            <dt>Monto girado</dt>
            <dd>{fmt(c.monto_girado)}</dd>
            <dt>Monto adeudado</dt>
            <dd>
              <b>{fmt(c.monto_adeudado)}</b> · {fmtUtm(c.utm_adeudadas)} UTM
            </dd>
            <dt>Prop. / Desc.</dt>
            <dd>{c.prop_desc}</dd>
            <dt>Inscripción</dt>
            <dd>
              Fojas {c.fojas} {c.fojas_ref === 'V' ? 'vuelta ' : ''}N° {c.rol_numero}, tipo{' '}
              {c.rol_tipo}, año {c.rol_anio}
            </dd>
            <dt>Folio nómina</dt>
            <dd className="mono">{c.folio_nomina}</dd>
          </dl>

          {minimo === 0 ? (
            <Alerta tono="warn" estilo={{ margin: 0 }}>
              Esta concesión aparece en la nómina con <b>monto adeudado $0</b> pese a tener{' '}
              {fmt(c.monto_girado)} girados. Con mínimo cero no hay aritmética de remate posible y el
              sistema no permite marcarla de interés. Solicita certificado de deuda a la TGR: o ya pagó y
              la nómina no se actualizó, o hay un error de la Tesorería.
            </Alerta>
          ) : null}

          {c.rol_tipo === 'T' ? (
            <Alerta tono="info" estilo={{ margin: '12px 0 0' }}>
              <b>Concesión en trámite.</b> No se adquiere una concesión constituida sino una
              situación jurídica todavía en formación. El puntaje la castiga en el criterio de
              solidez del título.
            </Alerta>
          ) : null}
        </div>

        {/* ---------------- Aritmética ---------------- */}
        <div className="sect">
          <h4>Aritmética del remate</h4>
          <dl className="dl">
            <dt>Mínimo de subasta</dt>
            <dd>
              <b>{fmt(minimo)}</b>{' '}
              <span className="sub">— art. 149: el mínimo es el valor de las patentes adeudadas</span>
            </dd>
            <dt>Garantía del postor</dt>
            <dd>
              {fmt(minimo)}{' '}
              <span className="sub">
                — art. 148: boleta por lo adeudado en <b>todo el lote</b>
                {c.lote ? '' : ', y hoy va sola'}
              </span>
            </dd>
            <dt>Costo de retiro para el dueño</dt>
            <dd>
              {fmt(c.costo_retiro_dueno)}{' '}
              <span className="sub">
                — art. 149 inc. 2: puede sacarla hasta el momento del remate pagando el doble
              </span>
            </dd>
            <dt>Plazo para enterar precio</dt>
            <dd>
              8 días desde el remate <span className="sub">— art. 152, o se pierde la caución</span>
            </dd>
          </dl>
        </div>

        {/* ---------------- Seguimiento ---------------- */}
        <div className="sect">
          <h4>Seguimiento</h4>

          <Fila2>
            <Campo etiqueta="Estado en el remate" estado={estadoCampo.estado} soloLectura={!puedeEditar}>
              <select
                value={borrador?.estado ?? c.estado}
                onChange={(e) => cambiarEstado(e.target.value as EstadoConcesion)}
              >
                {(Object.keys(ESTADOS) as EstadoConcesion[]).map((k) => (
                  <option key={k} value={k}>
                    {ESTADOS[k].t}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Nivel de interés" estado={estadoCampo.interes} soloLectura={!puedeEditar}>
              <select
                value={c.interes}
                onChange={(e) => void guardarCampo('interes', e.target.value as NivelInteres)}
              >
                {(Object.keys(INTERES) as NivelInteres[]).map((k) => (
                  <option key={k} value={k}>
                    {INTERES[k].t}
                  </option>
                ))}
              </select>
            </Campo>
          </Fila2>

          {/* Acreditación: el motor exige estos campos en la misma operación */}
          {borrador ? (
            <div className="card" style={{ background: 'var(--panel2)', marginBottom: 13 }}>
              {borrador.estado === 'RETIRADA' ? (
                <>
                  <Alerta tono="warn" estilo={{ marginBottom: 12 }}>
                    Para dar por retirada esta concesión hay que acreditar que el titular pagó{' '}
                    <b>{fmt(minimo * 2)}</b> — el doble de lo adeudado — a más tardar el día del
                    remate (art. 149 inc. 2). El sistema rechaza el cambio si el monto es menor.
                  </Alerta>
                  <Fila2>
                    <Campo etiqueta="Fecha del pago">
                      <input
                        type="date"
                        value={borrador.fecha_pago_patente}
                        max={fechaISO(c.fecha_remate) || undefined}
                        onChange={(e) =>
                          setBorrador({ ...borrador, fecha_pago_patente: e.target.value })
                        }
                      />
                    </Campo>
                    <Campo etiqueta={`Monto acreditado (mínimo ${fmt(minimo * 2)})`}>
                      <input
                        type="number"
                        value={borrador.monto_pagado}
                        onChange={(e) => setBorrador({ ...borrador, monto_pagado: e.target.value })}
                      />
                    </Campo>
                  </Fila2>
                </>
              ) : (
                <>
                  <Alerta tono="info" estilo={{ marginBottom: 12 }}>
                    Una adjudicación exige monto igual o superior al mínimo de subasta de{' '}
                    <b>{fmt(minimo)}</b>.
                    {borrador.estado === 'ADJ_NOSOTROS' ? (
                      <>
                        {' '}
                        Y quedan <b>8 días</b> para enterar el precio, o la adjudicación queda sin
                        efecto de pleno derecho y se pierde la caución (art. 152).
                      </>
                    ) : null}
                  </Alerta>
                  <Fila2>
                    <Campo etiqueta={`Monto de adjudicación (mínimo ${fmt(minimo)})`}>
                      <input
                        type="number"
                        value={borrador.monto_adjudicacion}
                        onChange={(e) =>
                          setBorrador({ ...borrador, monto_adjudicacion: e.target.value })
                        }
                      />
                    </Campo>
                    <Campo etiqueta="Adjudicatario">
                      <input
                        value={borrador.adjudicatario}
                        onChange={(e) => setBorrador({ ...borrador, adjudicatario: e.target.value })}
                      />
                    </Campo>
                  </Fila2>
                </>
              )}
              <div style={{ display: 'flex', gap: 9 }}>
                <button className="btn pri" onClick={() => void confirmarBorrador()}>
                  Registrar {ESTADOS[borrador.estado].t.toLowerCase()}
                </button>
                <button className="btn" onClick={() => setBorrador(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : null}

          {c.estado === 'RETIRADA' && !borrador ? (
            <Alerta tono="ok">
              Retirada del remate: el titular pagó <b>{fmt(c.monto_pagado)}</b> el{' '}
              {fecha(c.fecha_pago_patente)}, el doble de lo adeudado (art. 149 inc. 2). Sale del
              listado y del cálculo de presupuesto.
            </Alerta>
          ) : null}

          {(c.estado === 'ADJ_NOSOTROS' || c.estado === 'ADJ_TERCERO') && !borrador ? (
            <Alerta tono={c.estado === 'ADJ_NOSOTROS' ? 'ok' : 'info'}>
              Adjudicada en <b>{fmt(c.monto_adjudicacion)}</b>
              {c.adjudicatario ? <> a {c.adjudicatario}</> : null}.
              {c.estado === 'ADJ_NOSOTROS' ? (
                <>
                  {' '}
                  El precio debe enterarse dentro de 8 días desde el remate, más las costas en
                  proporción al precio (arts. 150 y 152).
                </>
              ) : null}
            </Alerta>
          ) : null}

          <Fila2>
            <Campo
              etiqueta="Postura máxima (CLP)"
              estado={estadoCampo.postura_max}
              soloLectura={!puedeEditar}
            >
              <input
                type="number"
                defaultValue={num(c.postura_max) || ''}
                placeholder={String(minimo)}
                onBlur={(e) => {
                  const v = Number(e.target.value || 0)
                  if (v !== num(c.postura_max)) void guardarCampo('postura_max', v)
                }}
              />
            </Campo>
            <Campo
              etiqueta="Superficie real (ha) — de la mensura"
              estado={estadoCampo.superficie_ha}
              soloLectura={!puedeEditar}
            >
              <input
                type="number"
                step="0.01"
                defaultValue={num(c.superficie_ha) || ''}
                placeholder="sin cargar"
                onBlur={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null
                  if (v !== (num(c.superficie_ha) || null)) void guardarCampo('superficie_ha', v)
                }}
              />
            </Campo>
          </Fila2>

          {!c.superficie_ha ? (
            <div className="sub" style={{ margin: '-6px 0 13px' }}>
              La superficie no se deriva del monto adeudado, aunque técnicamente se podría: la Ley
              21.420 reemplazó la tarifa plana por una escala progresiva según los años de vigencia,
              así que dos concesiones con la misma deuda pueden diferir en un orden de magnitud. Se
              llena a mano desde el plano de mensura.
            </div>
          ) : null}

          <Campo etiqueta="Notas" estado={estadoCampo.notas} soloLectura={!puedeEditar}>
            <textarea
              defaultValue={c.notas ?? ''}
              placeholder="Observaciones de terreno, contactos, antecedentes del titular…"
              onBlur={(e) => {
                if (e.target.value !== (c.notas ?? '')) void guardarCampo('notas', e.target.value)
              }}
            />
          </Campo>

          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn"
              disabled={!puedeEditar || verificar.isPending}
              onClick={() => verificar.mutateAsync(c.id).catch(avisar)}
            >
              {verificar.isPending ? 'Verificando…' : 'Marcar vigencia verificada'}
            </button>
            <span className="sub">
              {c.verificado_en
                ? `Verificada el ${fechaHora(c.verificado_en)}`
                : 'Sin verificar — el titular puede retirarla hasta el momento del remate'}
            </span>
          </div>
        </div>

        {/* ---------------- Puntaje ---------------- */}
        <div className="sect">
          <h4>Puntaje — criterios automáticos</h4>
          {automaticos.map((d) => (
            <div className="slider" key={d.codigo}>
              <label>
                {d.nombre} <span className="sub">· peso {d.peso}%</span>
              </label>
              <b>{num(d.valor).toFixed(1)}</b>
            </div>
          ))}

          <h4 style={{ marginTop: 18 }}>Puntaje — evaluación propia (1 a 5)</h4>
          {manualesDef.map((d) => (
            <div className="slider" key={d.codigo}>
              <label>
                {d.nombre} <span className="sub">· peso {d.peso}%</span>
                {!d.evaluado ? <span className="sub"> · sin evaluar, vale {NEUTRO}</span> : null}
                <br />
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  disabled={!puedeEditar}
                  value={manuales[d.codigo] ?? NEUTRO}
                  onChange={(e) =>
                    setManuales((prev) => ({ ...prev, [d.codigo]: Number(e.target.value) }))
                  }
                  onMouseUp={(e) =>
                    guardarEval
                      .mutateAsync({
                        id: c.id,
                        criterio: d.codigo,
                        valor: Number((e.target as HTMLInputElement).value),
                      })
                      .catch(avisar)
                  }
                  onTouchEnd={(e) =>
                    guardarEval
                      .mutateAsync({
                        id: c.id,
                        criterio: d.codigo,
                        valor: Number((e.target as HTMLInputElement).value),
                      })
                      .catch(avisar)
                  }
                />
              </label>
              <b>{manuales[d.codigo] ?? NEUTRO}</b>
            </div>
          ))}

          <div style={{ marginTop: 14, display: 'flex', gap: 9, alignItems: 'center' }}>
            <div className="bar" style={{ flex: 1, height: 8 }}>
              <i style={{ width: `${puntaje}%`, background: colorPuntaje(puntaje) }} />
            </div>
            <b style={{ fontSize: 17 }}>{puntaje}/100</b>
          </div>
          <div className="sub" style={{ marginTop: 6 }}>
            El puntaje es una herramienta de orden, no un veredicto. Los criterios manuales sin
            evaluar valen {NEUTRO} y su peso igual cuenta, para que los puntajes sean comparables
            entre una concesión evaluada y otra sin evaluar.
          </div>
        </div>

        {/* ---------------- Due diligence ---------------- */}
        <div className="sect">
          <h4>
            Due diligence
            {items.data ? (
              <span className="sub"> · {cumplidos.size} de {items.data.length}</span>
            ) : null}
          </h4>
          {(items.data ?? []).map((it) => (
            <label className="chk" key={it.id}>
              <input
                type="checkbox"
                disabled={!puedeEditar}
                checked={cumplidos.has(it.id)}
                onChange={(e) =>
                  guardarChk
                    .mutateAsync({ id: c.id, itemId: it.id, cumplido: e.target.checked })
                    .catch(avisar)
                }
              />
              <span>{it.texto}</span>
            </label>
          ))}
        </div>

        {/* ---------------- Corrección de la nómina (máster) ---------------- */}
        {rolUsuario === 'MASTER' ? <CorregirNomina concesion={c} /> : null}

        {/* ---------------- Historial ---------------- */}
        <div className="sect">
          <h4>Historial</h4>
          {historial.isLoading ? <Cargando texto="Leyendo la bitácora…" /> : null}
          {historial.data?.length ? (
            historial.data.map((h) => (
              <div className="hist" key={h.id}>
                {etiquetaCampo(h.campo)}: {h.valor_anterior ?? '—'} → {h.valor_nuevo ?? '—'}
                <br />
                <span>
                  {fechaHora(h.creado)} · {h.usuario_email ?? 'sistema'}
                </span>
              </div>
            ))
          ) : (
            <div className="sub">Sin movimientos registrados.</div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Corrección de los datos que vinieron de la nómina TGR. Solo máster.
 *
 * `concesion` es, en principio, un calco de lo que remitió la Tesorería y
 * no se toca: es la fuente de la aritmética del remate. Pero hay casos
 * concretos en que hay que corregirlo, y el más claro está en este mismo
 * listado — GEMINIS 1/59 figura con $1.146.180 girados y $0 adeudados.
 *
 * Cambiar el monto adeudado mueve el mínimo de subasta, el costo de retiro
 * del dueño, la garantía del art. 148, el percentil de escala y el calce
 * presupuestario de TODA la causa. Por eso va aparte del seguimiento, con
 * la advertencia visible y restringido por RLS.
 */
function CorregirNomina({ concesion }: { concesion: Concesion }) {
  const corregir = useCorregirConcesion()
  const avisar = useAvisar()
  const [abierto, setAbierto] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const [f, setF] = useState({
    nombre: concesion.nombre,
    ubicacion: concesion.ubicacion ?? '',
    titular: concesion.titular,
    monto_girado: String(num(concesion.monto_girado)),
    monto_adeudado: String(num(concesion.monto_adeudado)),
    utm_adeudadas: String(num(concesion.utm_adeudadas)),
    rol_tipo: concesion.rol_tipo,
  })

  const adeudado = Number(f.monto_adeudado) || 0
  const utm = Number(f.utm_adeudadas) || 0
  // En estas nóminas el monto adeudado es exactamente las UTM por $71.506.
  const utmImplicita = utm > 0 ? adeudado / utm : null
  const cuadra = utmImplicita === null || Math.abs(utmImplicita - num(concesion.valor_utm)) <= 5

  const guardar = async () => {
    try {
      await corregir.mutateAsync({
        id: concesion.id,
        cambio: {
          nombre: f.nombre.trim(),
          ubicacion: f.ubicacion.trim() || null,
          titular: f.titular.trim(),
          monto_girado: Number(f.monto_girado) || 0,
          monto_adeudado: adeudado,
          utm_adeudadas: utm,
          rol_tipo: f.rol_tipo,
        },
      })
      setGuardado(true)
      setTimeout(() => setGuardado(false), 3000)
    } catch (e) {
      avisar(e)
    }
  }

  if (!abierto) {
    return (
      <div className="sect">
        <h4>Datos de la nómina</h4>
        <p className="sub" style={{ marginBottom: 10 }}>
          Estos datos son un calco de lo que remitió la Tesorería y normalmente no se tocan. Corregilos
          solo con un documento que lo respalde — un certificado de deuda, por ejemplo.
        </p>
        <button className="btn" onClick={() => setAbierto(true)}>
          Corregir datos de la nómina
        </button>
      </div>
    )
  }

  return (
    <div className="sect">
      <h4>
        Corregir datos de la nómina
        {guardado ? <span className="estado-campo ok">guardado</span> : null}
      </h4>

      <Alerta tono="bad" estilo={{ marginBottom: 14 }}>
        <b>Esto mueve la aritmética del remate.</b> El monto adeudado <b>es</b> el mínimo de subasta
        (art. 149), y de él salen el costo de retiro del dueño, la garantía del art. 148 y el puntaje
        de las {39} concesiones de la causa. Cambialo solo con respaldo documental.
      </Alerta>

      <Fila2>
        <Campo etiqueta="Nombre">
          <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
        </Campo>
        <Campo etiqueta="Ubicación">
          <input value={f.ubicacion} onChange={(e) => setF({ ...f, ubicacion: e.target.value })} />
        </Campo>
      </Fila2>

      <Campo etiqueta="Titular">
        <input value={f.titular} onChange={(e) => setF({ ...f, titular: e.target.value })} />
      </Campo>

      <Fila2>
        <Campo etiqueta="Monto girado">
          <input
            type="number"
            value={f.monto_girado}
            onChange={(e) => setF({ ...f, monto_girado: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Monto adeudado = mínimo de subasta">
          <input
            type="number"
            value={f.monto_adeudado}
            onChange={(e) => setF({ ...f, monto_adeudado: e.target.value })}
          />
        </Campo>
      </Fila2>

      <Fila2>
        <Campo etiqueta="UTM adeudadas">
          <input
            type="number"
            step="0.0001"
            value={f.utm_adeudadas}
            onChange={(e) => setF({ ...f, utm_adeudadas: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Tipo de rol">
          <select
            value={f.rol_tipo}
            onChange={(e) => setF({ ...f, rol_tipo: e.target.value as 'C' | 'T' })}
          >
            <option value="C">C — constituida</option>
            <option value="T">T — en trámite</option>
          </select>
        </Campo>
      </Fila2>

      {/* Cuadratura contra la UTM de la nómina: en las 39 filas originales el
          monto adeudado es exactamente las UTM por $71.506, con ±$5 de
          redondeo. Si una corrección rompe esa relación, conviene mirarla. */}
      {utmImplicita !== null ? (
        <Alerta tono={cuadra ? 'ok' : 'warn'} estilo={{ marginTop: 4 }}>
          UTM implícita: <b>{fmt(Math.round(utmImplicita))}</b> por UTM.{' '}
          {cuadra ? (
            <>Cuadra con la nómina ({fmt(concesion.valor_utm)}).</>
          ) : (
            <>
              La nómina {concesion.folio_nomina} usa {fmt(concesion.valor_utm)}. Revisa si el monto o
              las UTM están mal: en las 39 filas originales la relación es exacta.
            </>
          )}
        </Alerta>
      ) : null}

      {adeudado === 0 ? (
        <Alerta tono="warn">
          Con monto adeudado <b>$0</b> el mínimo de subasta sería cero y el sistema no permite marcar
          la concesión de interés. Es exactamente la situación de la que hay que salir.
        </Alerta>
      ) : null}

      <div style={{ display: 'flex', gap: 9, marginTop: 6 }}>
        <button className="btn pri" disabled={corregir.isPending} onClick={() => void guardar()}>
          {corregir.isPending ? 'Guardando…' : 'Guardar corrección'}
        </button>
        <button className="btn" onClick={() => setAbierto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}

const CAMPOS: Record<string, string> = {
  estado: 'Estado',
  interes: 'Interés',
  postura_max: 'Postura máxima',
  superficie_ha: 'Superficie',
  fecha_pago_patente: 'Fecha de pago',
  monto_pagado: 'Monto pagado',
  monto_adjudicacion: 'Monto de adjudicación',
  adjudicatario: 'Adjudicatario',
  notas: 'Notas',
}

const etiquetaCampo = (campo: string | null) => (campo ? (CAMPOS[campo] ?? campo) : 'Cambio')
